import { authPublicApi } from '../../services/auth-public.api';
import { authService } from '../../services/auth';
import { resolveApiErrorMessage } from '../../services/api-errors';
import { i18nService, onLocaleChange, t } from '../../services/i18n';
import './rc-login-view';
import { RcLoginView } from './rc-login-view';

export class RcLogin extends HTMLElement {
  private disposeLocaleChange?: () => void;
  private isSubmittingNotification = false;
  private emailEnabled = false;
  private emailExpanded = false;
  private emailInput = '';
  private notificationError = '';
  private notificationSuccess = '';
  private hasSubmittedNotification = false;
  private providersReady = false;
  private googleEnabled = false;
  private microsoftEnabled = false;
  private providerError = '';

  connectedCallback(): void {
    this.addEventListener('input', ((event: Event) => {
      const target = event.target as HTMLInputElement | null;
      if (target?.matches?.('.login-email-input')) {
        this.emailInput = target.value;
      }
    }) as EventListener);
    this.addEventListener('login-email', (() => {
      void authService.requestEmailLogin(this.emailInput);
    }) as EventListener);
    this.addEventListener('login-email-toggle', (() => {
      this.emailExpanded = !this.emailExpanded;
      this.render();
    }) as EventListener);
    this.addEventListener('login-google', () => authService.loginWithGoogle());
    this.addEventListener('login-microsoft', () => authService.loginWithMicrosoft());
    this.addEventListener('login-dismiss-error', (() => {
      authService.clearLoginError();
      authService.dismissLogoutNotice();
      this.notificationError = '';
      this.notificationSuccess = '';
      this.hasSubmittedNotification = false;
      this.isSubmittingNotification = false;
      this.render();
    }) as EventListener);
    this.addEventListener('signup-notification-submit', (() => {
      void this.submitSignupNotification();
    }) as EventListener);
    this.render();
    authService.onLoginStateChange(() => this.render());
    this.disposeLocaleChange = onLocaleChange(() => this.render());
    void this.bootstrap();
  }

  disconnectedCallback(): void {
    this.disposeLocaleChange?.();
  }

  private render(): void {
    this.innerHTML = '<rc-login-view></rc-login-view>';
    const view = this.querySelector('rc-login-view') as RcLoginView | null;
    if (!view) return;
    const signupDisabledDetails = authService.currentSignupDisabledDetails;
    view.loading = authService.isLoggingIn;
    view.error = authService.currentLoginError;
    view.errorCode = authService.currentLoginErrorCode;
    view.signupNotificationEmail = signupDisabledDetails?.email || '';
    view.signupNotificationSubmitting = this.isSubmittingNotification;
    view.signupNotificationError = this.notificationError;
    view.signupNotificationSuccess = this.notificationSuccess;
    view.signupNotificationSubmitted = this.hasSubmittedNotification;
    view.providersReady = this.providersReady;
    view.emailEnabled = this.emailEnabled;
    view.emailExpanded = this.emailExpanded;
    view.emailInput = this.emailInput;
    view.success = authService.currentLoginSuccess;
    view.googleEnabled = this.googleEnabled;
    view.microsoftEnabled = this.microsoftEnabled;
    view.providerError = this.providerError;
    view.loggedOutNotice = authService.currentLogoutNoticeCode
      ? resolveApiErrorMessage({ code: authService.currentLogoutNoticeCode }, 403)
      : '';
    view.render();
  }

  private async bootstrap(): Promise<void> {
    const emailLoginToken = this.consumeEmailLoginToken();
    if (emailLoginToken) {
      await authService.completeEmailLogin(emailLoginToken);
    }

    if (!authService.isAuthenticated) {
      await this.initProviders();
    }
  }

  private consumeEmailLoginToken(): string | null {
    const url = new URL(window.location.href);
    const token = url.searchParams.get('emailLoginToken')?.trim() || null;
    if (!token) {
      return null;
    }

    url.searchParams.delete('emailLoginToken');
    window.history.replaceState({}, '', `${url.pathname}${url.search}${url.hash}`);
    return token;
  }

  private async initProviders(): Promise<void> {
    this.providersReady = false;
    this.providerError = '';
    this.render();

    try {
      const providers = await authPublicApi.getLoginProviders();
      this.emailEnabled = providers.emailEnabled === true;
      if (!this.emailEnabled) {
        this.emailExpanded = false;
      }
      this.googleEnabled = providers.googleEnabled === true && !!providers.googleClientId;
      this.microsoftEnabled = providers.microsoftEnabled === true && !!providers.microsoftClientId;

      if (this.googleEnabled) {
        await authService.initGoogleLogin(providers.googleClientId!);
      }

      if (this.microsoftEnabled) {
        await authService.initMicrosoftLogin(providers.microsoftClientId!);
      }
    } catch {
      this.emailEnabled = false;
      this.emailExpanded = false;
      this.googleEnabled = false;
      this.microsoftEnabled = false;
      this.providerError = t('auth.loginUnavailable');
    } finally {
      this.providersReady = true;
      this.render();
    }
  }

  private async submitSignupNotification(): Promise<void> {
    if (this.isSubmittingNotification || authService.currentLoginErrorCode !== 'signup_disabled') {
      return;
    }

    const signupDisabledDetails = authService.currentSignupDisabledDetails;
    if (!signupDisabledDetails?.email || !signupDisabledDetails.notificationToken) {
      this.notificationError = t('auth.signupNotification.missingContext');
      this.notificationSuccess = '';
      this.hasSubmittedNotification = false;
      this.render();
      return;
    }

    this.isSubmittingNotification = true;
    this.notificationError = '';
    this.notificationSuccess = '';
    this.hasSubmittedNotification = false;
    this.render();

    try {
      await authPublicApi.requestNewUserAccessNotification({
        notificationToken: signupDisabledDetails.notificationToken,
        preferredLocale: i18nService.currentLocale,
      });
      this.notificationSuccess = t('auth.signupNotification.success');
      this.hasSubmittedNotification = true;
    } catch (error: any) {
      this.notificationError = error?.message || t('auth.signupNotification.saveError');
      this.hasSubmittedNotification = false;
    } finally {
      this.isSubmittingNotification = false;
      this.render();
    }
  }
}

if (!customElements.get('rc-login')) {
  customElements.define('rc-login', RcLogin);
}
